/**
 * Reference-counted shared microphone capture.
 *
 * Background (see docs/superpowers/specs/2026-05-16-concurrent-calls-mediamanager-design.md):
 * the app supports being in TWO calls at once (group voice channel + 1:1
 * DM call). Each call has its own LiveKit Room and publishes the mic
 * independently — so independent mute toggles work. But the underlying
 * hardware (getUserMedia) and the optional denoiser graph (RNNoise /
 * DeepFilterNet) should run ONCE per device/NS-preference combo, not
 * per room. Two parallel getUserMedia calls cost CPU and battery, and
 * two denoiser graphs on the same source waste work.
 *
 * Design: a per-process Map keyed by `(deviceId, noiseSuppression)`. Each
 * entry holds the raw capture + denoiser graph + ref count. Callers
 * `acquire` to get a `MicHandle` (with a cloned publish-ready track
 * that they own), and `release` to drop their reference. When the last
 * handle for a capture releases, the capture is fully torn down.
 *
 * Track cloning rationale: `MediaStreamTrack.clone()` creates a new
 * track that draws from the same underlying source. Each room can
 * publish its own clone, mute/unmute it independently, and `stop()` it
 * on disconnect without affecting the source — that stays alive until
 * `release()` decides nobody else needs it.
 */

import { loadDenoiserBackend, type DenoiserHandle, type DenoiserBackendName } from './denoiserBackend'
import { applyAgcOnly } from './agcOnly'
import { requestSendAgcFade } from './sendAgc'

export type DenoiserStatus =
  | 'rnnoise'   // RNNoise WASM worklet is processing the mic (default + only supported denoiser since DFN removal 1.1.177A)
  | 'agc-only'  // sendAgc graph only (no denoiser) — see invariant #57. Used when NS is off OR the denoiser failed: sendAgc still levels the outgoing mic so peers arrive consistent.
  | 'browser'   // no graph; relying on browser-native NS via getUserMedia constraints (last-resort: AGC-only also failed to build)
  | 'off'       // user disabled noise suppression AND AGC-only fallback unavailable (kill-switch / ctx suspended)
  | 'idle'      // no active capture yet

export interface AcquireOptions {
  /** Specific input device ID, or undefined for browser default. */
  deviceId?: string
  /**
   * When true, request the RNNoise denoiser graph (falls back to AGC-only
   * if the worklet fails to load, or to raw publish via 'browser' if both
   * fail). When false, request a raw mic track with `noiseSuppression: false`
   * (denoiser='off').
   */
  noiseSuppression: boolean
  /**
   * Optional namespace so two callers on the SAME (deviceId, noiseSuppression)
   * get SEPARATE captures instead of sharing one. Used to isolate a DM call's
   * mic from the channel's so a channel mute/teardown can't kill the DM track
   * (proven root cause: a channel mute unpublished the DM mic with ZERO DM-side
   * setMicrophoneEnabled calls — the shared single-capture teardown). Omitted /
   * empty = shared (default, original behaviour).
   */
  isolationKey?: string
}

export interface MicHandle {
  /** Clone of the active mic track, owned by this handle. Publish to LiveKit; release on hangup. NOTE: when a handle is acquired DURING the AGC-only stopgap window (invariant #58), this is a clone of the AGC-only graph's output; when the background denoiser swap completes the caller receives a FRESH clone via `subscribeTrackSwap` and must `replaceTrack` to migrate the live LiveKit publication to the denoiser graph. */
  readonly track: MediaStreamTrack
  /** Backend currently running ('rnnoise'/'agc-only'/'browser'/'off'). May change once during a session via the AGC-only → denoiser stopgap swap (#58). */
  readonly denoiserState: DenoiserStatus
  /** Subscribe to denoiser-state changes on the underlying shared capture. Returns unsubscribe fn. */
  subscribeDenoiser(fn: (state: DenoiserStatus) => void): () => void
  /**
   * Subscribe to mid-stream publish-track swaps. Fires when the AGC-only
   * interim graph (published immediately on acquire, #58) hands off to the
   * background-loaded denoiser graph. The callback receives a FRESH clone
   * of the new publishSourceTrack — the caller is responsible for swapping
   * the live LiveKit publication via `LocalAudioTrack.replaceTrack(newTrack)`
   * and (optionally) stopping the old `handle.track`. Fires AT MOST ONCE per
   * handle (capture only ever swaps AGC→denoiser, never back). Returns
   * unsubscribe. If the swap already happened by the time you subscribe
   * (race), the callback fires once on the next microtask with the current
   * denoiser-clone — you never miss the swap.
   */
  subscribeTrackSwap(fn: (newTrack: MediaStreamTrack) => void): () => void
  /**
   * Release this handle. Stops the cloned track and decrements the
   * shared capture's refCount. When refCount hits 0, the entire
   * capture (raw track, denoiser graph) is destroyed.
   *
   * `graceMs` (default 0 = destroy immediately) keeps the underlying
   * capture warm for that many ms after the last handle releases, so a
   * fast re-`acquireMic` for the same (device, NS) reuses it instead of
   * paying a fresh getUserMedia + denoiser rebuild. Used for room
   * switches (leave room A → join room B). MUST stay 0 for mute, where
   * invariant §28 requires the OS capture to end promptly (a held
   * capture triggers Windows comms ducking of all other audio).
   */
  release(opts?: { graceMs?: number }): void
}

interface ActiveCapture {
  key: string
  refCount: number
  rawTrack: MediaStreamTrack
  /** Track that handles clone from. == rawTrack only as a LAST RESORT when both the denoiser cascade AND the AGC-only fallback fail (status='browser') — otherwise points at a processed track. */
  publishSourceTrack: MediaStreamTrack
  denoiserHandle: DenoiserHandle | null
  /**
   * sendAgc-only graph used when the denoiser is off / failed / bypassed.
   * Built via `applyAgcOnly` so the outgoing mic still goes through the
   * per-user loudness AGC even when no denoiser is running (invariant #57).
   * null when denoiserHandle is set (sendAgc lives inside the denoiser graph)
   * or when AGC-only also failed to build (status falls to 'browser').
   */
  agcOnlyHandle: DenoiserHandle | null
  /**
   * Interim AGC-only graph published BEFORE the denoiser cascade completes
   * (invariant #58 — AGC-only stopgap). Distinct from `agcOnlyHandle`
   * (#57: TERMINAL state when denoiser is off/failed). When the background
   * cascade returns a denoiser handle, the swap path: notifies handles,
   * tears down this stopgap (its rawTrack-clone is stopped — NOT the master
   * `rawTrack`), nulls this field. The AGC graph runs on a CLONE of
   * `rawTrack` so its destroy doesn't kill the master mic that the denoiser
   * backend is awaiting on.
   */
  agcStopgapHandle: DenoiserHandle | null
  /**
   * Per-handle track-swap listeners. Fires once when AGC stopgap → denoiser
   * handoff completes; each subscriber receives a FRESH clone of the new
   * denoiser publishSourceTrack. Empty after swap completes + all
   * subscribers acknowledged.
   */
  trackSwapListeners: Map<InternalHandle, (newTrack: MediaStreamTrack) => void>
  denoiserState: DenoiserStatus
  denoiserListeners: Set<(state: DenoiserStatus) => void>
  /** Outstanding handles. Used so release() can find + remove the right one. */
  handles: Set<InternalHandle>
  /**
   * True when the user requested noise suppression but every backend
   * in the cascade failed during buildCapture (browser-native NS is
   * active as a fallback). `acquireMic` refuses to reuse a degraded
   * capture so the next mute/unmute cycle gets a fresh shot at
   * loading the worklet — fixes the user-reported "RNN не подцепляется,
   * приходится рефрешить страницу" symptom where the legacy mediaManager
   * silently retried on each setMicrophoneEnabled call but the cached
   * SharedMicCapture stuck the bad state for the whole session.
   */
  denoiserDegraded: boolean
  /**
   * Pending teardown timer set when the last handle released with a
   * grace window (room switch). While non-null the capture is "warm but
   * idle" (refCount 0) — a re-acquire for the same key cancels it and
   * reuses the capture. Cleared on reuse and in destroyCapture.
   */
  graceTimer: ReturnType<typeof setTimeout> | null
}

interface InternalHandle extends MicHandle {
  /** Internal capture pointer for release bookkeeping. */
  __capture: ActiveCapture
  /**
   * True when this handle was acquired DURING the AGC-only stopgap window
   * (#58) — i.e. before the background denoiser cascade had swapped in.
   * Determines whether `subscribeTrackSwap` may fire (post-swap) for this
   * handle. Handles acquired AFTER the swap already get a denoiser clone
   * at acquire time and don't need a swap.
   */
  __bornUnderStopgap: boolean
}

const captures = new Map<string, ActiveCapture>()
/** Outstanding in-flight acquires keyed by capture key — coalesces concurrent acquires for the same combo. */
const inflight = new Map<string, Promise<ActiveCapture>>()

// The concrete deviceId we last physically captured. When a caller asks for the
// OS "default" mic (deviceId undefined OR the literal 'default'), we substitute
// this pinned id so a LATER acquire (a 2nd DM call, an unmute) re-captures the
// SAME physical device instead of whatever Windows' "default COMMUNICATIONS"
// device has since flipped to. Proven prod repro (2026-05-22): the mic was on
// "Default"; the 1st DM call captured fine, but the 2nd call's 'default'
// re-resolved to a flipped/silent input → `out_bitrate_kbps>0` with
// `total_energy=0` (publishing silence). The channel/legacy path already pins
// via mediaManager `_pinnedMicDeviceId` (#39); this is the same protection for
// the per-room sharedMicCapture path (channel + DM). Page-lifetime, not
// persisted. Kill-switch: localStorage 'voiceMicPinDevice'='false'.
let pinnedDeviceId: string | null = null
function micPinningOn(): boolean {
  return typeof window === 'undefined' || localStorage.getItem('voiceMicPinDevice') !== 'false'
}
function isDefaultDeviceRequest(deviceId?: string): boolean {
  return !deviceId || deviceId === 'default'
}

function captureKey(opts: AcquireOptions): string {
  return `${opts.deviceId ?? '__default__'}|${opts.noiseSuppression ? '1' : '0'}|${opts.isolationKey ?? ''}`
}

function setDenoiserState(capture: ActiveCapture, next: DenoiserStatus): void {
  if (capture.denoiserState === next) return
  capture.denoiserState = next
  for (const fn of capture.denoiserListeners) {
    try { fn(next) } catch { /* never let one buggy subscriber kill notify */ }
  }
}

async function buildCapture(key: string, opts: AcquireOptions): Promise<ActiveCapture> {
  // 1. Open the raw capture. NS-off path requests bare audio so the
  //    browser doesn't apply its own filter chain on top of our (none)
  //    processing. NS-on path also requests `noiseSuppression: false`
  //    because the denoiser does its own NS — stacking browser NS on
  //    top costs CPU and dulls consonants.
  const buildConstraints = (dev?: string): MediaStreamConstraints => ({
    audio: {
      noiseSuppression: false,
      echoCancellation: true,
      autoGainControl: true,
      ...(dev ? { deviceId: { exact: dev } } : {}),
    },
  })
  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia(buildConstraints(opts.deviceId))
  } catch (err) {
    // The requested (often pinned) device vanished or is held by another app —
    // OverconstrainedError / NotFoundError / NotReadableError. Don't leave the
    // user mic-less: drop the pin and re-capture the OS default so they're
    // audible; the next acquire re-pins to whatever we actually get. Mirrors
    // mediaManager #39's fallback for the legacy path.
    const name = (err as Error)?.name || ''
    if (opts.deviceId && /OverconstrainedError|NotFoundError|NotReadableError/i.test(name)) {
      console.warn(`[sharedMicCapture] mic device '${opts.deviceId}' unavailable (${name}); falling back to OS default`)
      if (pinnedDeviceId === opts.deviceId) pinnedDeviceId = null
      stream = await navigator.mediaDevices.getUserMedia(buildConstraints(undefined))
    } else {
      throw err
    }
  }
  const rawTrack = stream.getAudioTracks()[0]
  if (!rawTrack) throw new Error('getUserMedia returned no audio track')
  // Pin the concrete device we ACTUALLY captured so a later "default" request
  // re-uses it instead of re-resolving the (flipping) OS comms-default. Only a
  // real concrete id — never the literal 'default'. See pinnedDeviceId above.
  if (micPinningOn()) {
    const capturedId = rawTrack.getSettings().deviceId
    if (capturedId && capturedId !== 'default') pinnedDeviceId = capturedId
  }

  const capture: ActiveCapture = {
    key,
    refCount: 0,
    rawTrack,
    publishSourceTrack: rawTrack,
    denoiserHandle: null,
    agcOnlyHandle: null,
    agcStopgapHandle: null,
    trackSwapListeners: new Map(),
    denoiserState: 'idle',
    denoiserListeners: new Set(),
    handles: new Set(),
    denoiserDegraded: false,
    graceTimer: null,
  }

  // Startup fade-in (invariant #51): the next createSendAgc consumes this
  // pending fade so the first ~1 s of a fresh capture is silent→ramp,
  // swallowing the capture/denoiser/AGC startup transient. This was wired
  // ONLY in the legacy applyDenoiserMic path; the per-room sharedMicCapture
  // (the now-default mic path for BOTH channel and DM) skipped it, so the
  // mic-enable transient leaked to peers. Tunable: localStorage
  // 'voiceMicFadeInMs' (default 1000, 0 disables).
  try {
    const fadeMs = (() => {
      if (typeof localStorage === 'undefined') return 1000
      const v = parseInt(localStorage.getItem('voiceMicFadeInMs') || '', 10)
      return Number.isFinite(v) ? v : 1000
    })()
    requestSendAgcFade(fadeMs)
  } catch { /* noop */ }

  if (!opts.noiseSuppression) {
    // User explicitly disabled noise suppression — but loudness leveling
    // (sendAgc) is a SEPARATE concern that should still apply (invariant #57).
    // Try the AGC-only graph; only if THAT also fails fall back to status='off'
    // (raw publish, no leveling). Kill-switch: localStorage
    // 'voiceAgcWhenNoDenoiser'='false' restores the old raw-publish behaviour.
    const agcOn =
      typeof localStorage === 'undefined' || localStorage.getItem('voiceAgcWhenNoDenoiser') !== 'false'
    if (agcOn) {
      try {
        const agc = await applyAgcOnly(rawTrack)
        capture.agcOnlyHandle = agc
        capture.publishSourceTrack = agc.track
        setDenoiserState(capture, 'agc-only')
        return capture
      } catch (err) {
        console.warn('[sharedMicCapture] AGC-only graph build failed (NS off path); falling back to raw publish:', err)
      }
    }
    setDenoiserState(capture, 'off')
    return capture
  }

  // 2. AGC-ONLY STOPGAP (invariant #58). The original flow awaited the full
  //    denoiser cascade BEFORE publishing — so any cold-start / re-apply
  //    delay published the raw mic with no leveling for ~10-15s on DFN
  //    cold-start (was patched #46 with RNNoise stopgap), AND for the brief
  //    RNNoise WASM load window on every fresh capture. The 2026-05-26
  //    voice_diag investigation found ~15k `denoiser='idle'` samples
  //    in 60 minutes across 15 users — those windows leak hot audio without
  //    sendAgc leveling. Solution mirrors mediaManager #46 RNNoise stopgap
  //    for DFN: publish AGC-only NOW (instant, leveled, no NS yet), kick the
  //    denoiser cascade in BACKGROUND, swap the published track when ready.
  //    Kill-switch: localStorage 'voiceAgcStopgap'='false' restores the
  //    old await-denoiser-before-publish behaviour.
  const stopgapOn =
    typeof localStorage === 'undefined' || localStorage.getItem('voiceAgcStopgap') !== 'false'
  if (stopgapOn) {
    try {
      // AGC graph operates on a CLONE of rawTrack so AGC.destroy() (on swap)
      // stops the clone — NOT the master rawTrack which the denoiser
      // backend uses (rnnoise.ts / deepfilternet.ts take rawTrack directly).
      // Two independent MediaStreamTracks from the same OS mic source —
      // standard WebRTC pattern, no double-getUserMedia.
      const agcRawClone = rawTrack.clone()
      const agc = await applyAgcOnly(agcRawClone)
      capture.agcStopgapHandle = agc
      capture.publishSourceTrack = agc.track
      setDenoiserState(capture, 'agc-only')
      // Kick the background swap. Awaited in fire-and-forget pattern —
      // buildCapture returns NOW with the AGC-only track ready to publish.
      void backgroundDenoiserSwap(capture)
      return capture
    } catch (err) {
      console.warn('[sharedMicCapture] AGC stopgap build failed; falling back to await-denoiser path:', err)
      // fall through to the synchronous await-cascade path below
    }
  }

  // 3. SYNCHRONOUS DENOISER CASCADE (legacy path: stopgap disabled OR stopgap
  //    itself failed). RNNoise is the only supported backend since DFN
  //    removal in 1.1.177A (see `feedback_dfn_capability_gate` for the
  //    history). If RNNoise itself fails, the cascade falls through to
  //    the AGC-only graph (#57), and then to raw publish ('browser') as
  //    last resort.
  const cascade: DenoiserBackendName[] = ['rnnoise']

  let handle: DenoiserHandle | null = null
  let usedBackend: DenoiserBackendName | null = null
  for (const name of cascade) {
    try {
      const backend = await loadDenoiserBackend(name)
      handle = await backend.apply(rawTrack)
      if (handle) {
        usedBackend = name
        break
      }
    } catch (err) {
      console.error(`[sharedMicCapture] denoiser '${name}' init threw:`, err)
    }
  }

  if (handle && usedBackend) {
    capture.denoiserHandle = handle
    capture.publishSourceTrack = handle.track
    setDenoiserState(capture, usedBackend)
  } else {
    // All cascade entries failed. The denoiser is gone for this session,
    // but loudness leveling (sendAgc) is a separate concern — try the
    // AGC-only graph so the outgoing mic is still leveled to the common
    // target. Only when AGC-only ALSO fails do we surface as 'browser'
    // and publish the raw track (invariant #57). Either way the capture
    // is flagged degraded so the next acquire retries the denoiser.
    // Kill-switch: localStorage 'voiceAgcWhenNoDenoiser'='false' skips the
    // AGC-only fallback (old raw-publish behaviour).
    capture.denoiserDegraded = true
    const agcOn =
      typeof localStorage === 'undefined' || localStorage.getItem('voiceAgcWhenNoDenoiser') !== 'false'
    if (agcOn) {
      try {
        const agc = await applyAgcOnly(rawTrack)
        capture.agcOnlyHandle = agc
        capture.publishSourceTrack = agc.track
        setDenoiserState(capture, 'agc-only')
        return capture
      } catch (err) {
        console.warn('[sharedMicCapture] AGC-only graph build failed (cascade-failed path); falling back to browser NS:', err)
      }
    }
    setDenoiserState(capture, 'browser')
  }

  return capture
}

/**
 * Background denoiser cascade for the AGC-only stopgap path (#58).
 * Runs after `buildCapture` returns the capture in 'agc-only' state.
 * When the cascade succeeds:
 *  - swap `publishSourceTrack` to the denoiser graph's output
 *  - notify each handle subscribed via `subscribeTrackSwap` with a fresh
 *    clone of the new source (caller does `LocalAudioTrack.replaceTrack`)
 *  - destroy the AGC stopgap (its rawTrack-clone is stopped; master
 *    `rawTrack` lives on for the denoiser)
 *  - update `denoiserState` to the chosen backend
 * When the cascade fails entirely: stay on AGC-only (still leveled),
 * mark `denoiserDegraded` so the next acquire retries.
 */
async function backgroundDenoiserSwap(capture: ActiveCapture): Promise<void> {
  // ⚠ DO NOT add an entry-time `captures.get(capture.key) !== capture` bail
  // check here. `void backgroundDenoiserSwap(capture)` is called SYNCHRONOUSLY
  // from inside buildCapture, BEFORE buildCapture's promise resolves and
  // acquireMic's `.then((c) => captures.set(key, c))` runs. An entry check
  // would therefore ALWAYS see `captures.get(key) === undefined` and bail
  // immediately — the swap would NEVER fire. Proven from prod 2026-05-26
  // (Aleksei I + Dmitriy on 1.1.170A: voice_diag denoiser_backend='agc-only'
  // 100% of samples; ZERO `[sharedMicCapture] AGC stopgap` console logs
  // anywhere). Capture-destroyed liveness IS still re-checked after EACH
  // await below (loadDenoiserBackend, backend.apply), so a teardown
  // mid-cascade still tears down cleanly with the freshly-built handle
  // destroyed in the post-await branch.
  // RNNoise-only cascade since DFN removal (1.1.177A).
  const cascade: DenoiserBackendName[] = ['rnnoise']

  let handle: DenoiserHandle | null = null
  let usedBackend: DenoiserBackendName | null = null
  for (const name of cascade) {
    try {
      const backend = await loadDenoiserBackend(name)
      // Each cascade entry can be slow (DFN: ~10-15s cold-start). Re-check
      // capture liveness after each await so a destroyed-mid-cascade case
      // tears down cleanly instead of installing a dead handle.
      if (captures.get(capture.key) !== capture) {
        return
      }
      handle = await backend.apply(capture.rawTrack)
      if (captures.get(capture.key) !== capture) {
        // Destroyed mid-apply — tear down what we built and abort.
        if (handle) try { handle.destroy() } catch { /* noop */ }
        return
      }
      if (handle) {
        usedBackend = name
        break
      }
    } catch (err) {
      console.error(`[sharedMicCapture stopgap] denoiser '${name}' init threw:`, err)
    }
  }

  if (handle && usedBackend) {
    const oldStopgap = capture.agcStopgapHandle
    capture.denoiserHandle = handle
    capture.publishSourceTrack = handle.track
    capture.agcStopgapHandle = null
    setDenoiserState(capture, usedBackend)

    // Notify each handle born under the stopgap so it can replaceTrack on
    // its live LiveKit publication. Each gets a FRESH clone from the new
    // denoiser source (independent of any other handle's clone). One-shot:
    // after firing, drop the listener entry. Handles acquired AFTER swap
    // already get a denoiser clone at acquire-time and weren't registered.
    for (const fn of capture.trackSwapListeners.values()) {
      try {
        const newClone = handle.track.clone()
        fn(newClone)
      } catch (err) {
        console.warn('[sharedMicCapture] track-swap notify failed:', err)
      }
    }
    capture.trackSwapListeners.clear()

    // Tear down the AGC stopgap. Its destroy stops the agcRawClone — NOT
    // the master capture.rawTrack which the denoiser is now using.
    if (oldStopgap) {
      try { oldStopgap.destroy() } catch { /* noop */ }
    }

    console.info(`[sharedMicCapture] AGC stopgap → ${usedBackend} swap complete.`)
  } else {
    // Cascade fully failed in the background. Stay on AGC-only (we're
    // already at denoiserState='agc-only' from buildCapture). Mark degraded
    // so a subsequent acquire (mute toggle / re-join) gets a fresh cascade.
    capture.denoiserDegraded = true
    console.warn('[sharedMicCapture] AGC stopgap kept (background denoiser cascade failed); capture marked degraded for next retry.')
  }
}

function destroyCapture(capture: ActiveCapture): void {
  if (capture.graceTimer !== null) {
    clearTimeout(capture.graceTimer)
    capture.graceTimer = null
  }
  if (capture.denoiserHandle) {
    try { capture.denoiserHandle.destroy() } catch { /* noop */ }
    capture.denoiserHandle = null
  }
  // AGC-only graph (when denoiser was off / failed). Same teardown
  // discipline as a denoiser handle — stops sendAgc loop, disconnects
  // nodes, stops the raw mic + processed track (#17).
  if (capture.agcOnlyHandle) {
    try { capture.agcOnlyHandle.destroy() } catch { /* noop */ }
    capture.agcOnlyHandle = null
  }
  // AGC stopgap (interim graph between buildCapture and the background
  // denoiser swap, #58). If we tear down before the swap completes, the
  // backgroundDenoiserSwap function checks `captures.get(capture.key) !==
  // capture` after each await and bails — but the stopgap handle still
  // needs cleanup here for the race where the swap is mid-fire.
  if (capture.agcStopgapHandle) {
    try { capture.agcStopgapHandle.destroy() } catch { /* noop */ }
    capture.agcStopgapHandle = null
  }
  capture.trackSwapListeners.clear()
  // Stop the raw track too. The denoiser/AGC destroy may already stop
  // the underlying source (rnnoise.ts + agcOnly.ts both do), but the
  // call is idempotent. NOTE: the stopgap path's AGC operates on a CLONE
  // of rawTrack, so stopgap-destroy doesn't end rawTrack — this final
  // rawTrack.stop() is what actually releases the OS mic in the stopgap
  // case.
  try { capture.rawTrack.stop() } catch { /* noop */ }
  setDenoiserState(capture, 'idle')
  capture.denoiserListeners.clear()
  capture.handles.clear()
  captures.delete(capture.key)
}

/**
 * Acquire a mic handle. Cheap on repeat calls for the same
 * (deviceId, noiseSuppression) — they share the underlying capture
 * and denoiser graph.
 *
 * Concurrent acquires for the same key coalesce into a single
 * `buildCapture` invocation; all callers see the resulting capture
 * once it's ready.
 */
export async function acquireMic(opts: AcquireOptions): Promise<MicHandle> {
  // Resolve an OS-"default" request to the pinned concrete device, so a 2nd
  // acquire (next DM call / unmute) re-captures the SAME physical mic instead
  // of the flipping Windows comms-default. See pinnedDeviceId. No-op for an
  // explicit device pick or when nothing is pinned yet (first capture).
  if (micPinningOn() && isDefaultDeviceRequest(opts.deviceId) && pinnedDeviceId) {
    opts = { ...opts, deviceId: pinnedDeviceId }
  }
  const key = captureKey(opts)
  let capture = captures.get(key)
  // If the cached capture is degraded (denoiser cascade failed on
  // build) AND nothing currently holds a handle, drop it so the next
  // build attempt gets a fresh shot at loading the RNNoise worklet.
  // We only do this when refCount=0 because a live capture in use by
  // another room would mean ripping its track out from under it; the
  // shorter-window optimisation is "next time everyone has released,
  // try again."
  if (capture && capture.denoiserDegraded && capture.refCount === 0 && opts.noiseSuppression) {
    destroyCapture(capture)
    capture = undefined
  }
  // Reusing a warm capture that was released with a grace window (room
  // switch): cancel its pending teardown so it isn't destroyed out from
  // under the handle we're about to hand back.
  if (capture && capture.graceTimer !== null) {
    clearTimeout(capture.graceTimer)
    capture.graceTimer = null
  }
  if (!capture) {
    let pending = inflight.get(key)
    if (!pending) {
      pending = buildCapture(key, opts)
        .then((c) => {
          captures.set(key, c)
          inflight.delete(key)
          return c
        })
        .catch((err) => {
          inflight.delete(key)
          throw err
        })
      inflight.set(key, pending)
    }
    capture = await pending
  }

  const track = capture.publishSourceTrack.clone()
  capture.refCount += 1
  // Stopgap-born handle? True when the capture is still in 'agc-only' state
  // AND a background swap is pending (agcStopgapHandle still set). Such a
  // handle MAY receive a one-shot track-swap notification when the
  // background denoiser cascade completes (#58). A handle acquired AFTER
  // the swap already cloned from the denoiser source — no swap needed.
  const bornUnderStopgap = capture.denoiserState === 'agc-only' && capture.agcStopgapHandle !== null

  const handle: InternalHandle = {
    __capture: capture,
    __bornUnderStopgap: bornUnderStopgap,
    track,
    denoiserState: capture.denoiserState,
    subscribeDenoiser(fn) {
      capture!.denoiserListeners.add(fn)
      fn(capture!.denoiserState)
      return () => { capture!.denoiserListeners.delete(fn) }
    },
    subscribeTrackSwap(fn: (newTrack: MediaStreamTrack) => void) {
      const self = this as InternalHandle
      // Three cases (#58):
      //  1. Handle was born under the stopgap AND swap hasn't happened yet
      //     (agcStopgapHandle still set): register for the future swap.
      //  2. Handle was born under the stopgap AND swap ALREADY happened
      //     (denoiserHandle set, agcStopgapHandle null, denoiserState !=
      //     'agc-only'): fire one-shot on next microtask with a fresh clone
      //     of the now-current denoiser publishSourceTrack.
      //  3. Handle was NOT born under the stopgap (acquired after swap, or
      //     stopgap was never used / failed): nothing to do — handle.track
      //     already points at the correct source.
      if (!self.__bornUnderStopgap) {
        return () => { /* noop */ }
      }
      if (capture!.agcStopgapHandle !== null) {
        // Case 1: register for future swap.
        capture!.trackSwapListeners.set(self, fn)
        return () => { capture!.trackSwapListeners.delete(self) }
      }
      // Case 2: swap already done — fire one-shot.
      const denoiserSource = capture!.denoiserHandle?.track ?? capture!.publishSourceTrack
      let cancelled = false
      queueMicrotask(() => {
        if (cancelled) return
        try {
          fn(denoiserSource.clone())
        } catch (err) {
          console.warn('[sharedMicCapture] subscribeTrackSwap one-shot notify failed:', err)
        }
      })
      return () => { cancelled = true }
    },
    release(opts?: { graceMs?: number }) {
      if (!capture!.handles.has(this)) return
      capture!.handles.delete(this)
      // Drop any pending track-swap subscription so a late background swap
      // doesn't fire onto a released handle (the LiveKit publication is gone).
      capture!.trackSwapListeners.delete(this)
      // Always stop THIS handle's cloned publish track immediately —
      // the published mic stops the moment the room unpublishes,
      // regardless of whether the shared source lingers for a grace
      // window. The OS-level raw capture is what §28 cares about, and
      // that only lives on while refCount > 0 OR a grace timer is
      // pending (room switch, never mute).
      try { track.stop() } catch { /* noop */ }
      capture!.refCount -= 1
      if (capture!.refCount <= 0) {
        const graceMs = opts?.graceMs ?? 0
        if (graceMs > 0) {
          const cap = capture!
          if (cap.graceTimer !== null) clearTimeout(cap.graceTimer)
          cap.graceTimer = setTimeout(() => {
            cap.graceTimer = null
            // A re-acquire during the window clears this timer; the
            // refCount guard covers the race where one slipped through.
            if (cap.refCount <= 0) destroyCapture(cap)
          }, graceMs)
        } else {
          destroyCapture(capture!)
        }
      }
    },
  }
  capture.handles.add(handle)
  return handle
}

/**
 * For tests + diagnostics. Returns the count of active shared captures
 * — should match the number of distinct (deviceId, NS-pref) combos
 * currently in use across all rooms.
 */
export function _diagnosticCaptureCount(): number {
  return captures.size
}

/**
 * For tests. Force-tear-down every active capture. Real code never
 * calls this — captures self-destruct via refCount.
 */
export function _resetForTests(): void {
  for (const capture of captures.values()) {
    destroyCapture(capture)
  }
  captures.clear()
  inflight.clear()
}
